import React, { useState, useEffect, Suspense, lazy } from 'react'
import { Box, Drawer, Typography, Avatar, Divider, IconButton, Chip, CircularProgress } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import CloseIcon from '@mui/icons-material/Close'
import VerifiedIcon from '@mui/icons-material/Verified'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import dayjs from 'dayjs'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import SupportQRCode from 'src/consumer/components/common/SupportQRCode'

// Lazy load WaitingEngagement for performance
const WaitingEngagement = lazy(() => 
  import('src/consumer/components/PublicLiveSessionPage/components/WaitingEngagement/WaitingEngagement')
)

const WaitingDrawer = ({
  open,
  sessionStartTime,
  mentorName,
  mentorProfilePicId,
  hasMentorJoined,
  supportQRCodeUrl = '/support-qr-code.png', // Default QR code path (fallback)
  supportQRCodeMediaId, // New: QR code media ID from clinic brand (preferred)
  onClose, // New: callback to close the drawer
  showOnlySupport = false, // New: if true, only show support section
  allowManualToggle = false, // New: if true, drawer can be manually toggled
  isCollapsed = false, // New: if true, drawer is off-screen but can be expanded
  engagementConfig = null, // Waiting screen engagement config (music tracks, games)
  sessionSlug = null, // Public session slug (for /v/* routes)
  bookedEventId = null, // Booked event ID (for /l/* routes)
}) => {
  const [timeUntilStart, setTimeUntilStart] = useState(null)
  const [waitTimeMs, setWaitTimeMs] = useState(0) // Wait time in milliseconds for engagement threshold
  const [isBeforeStartTime, setIsBeforeStartTime] = useState(false)

  useEffect(() => {
    if (!sessionStartTime) return

    const updateTimer = () => {
      const now = dayjs()
      const start = dayjs(sessionStartTime)
      const diff = start.diff(now)

      if (diff > 0) {
        // Session hasn't started yet
        setIsBeforeStartTime(true)
        setWaitTimeMs(diff) // Store milliseconds for engagement threshold
        // Calculate minutes and seconds from milliseconds (dayjs doesn't have duration)
        const totalSeconds = Math.floor(diff / 1000)
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
        const seconds = String(totalSeconds % 60).padStart(2, '0')
        setTimeUntilStart(`${minutes}:${seconds}`)
      } else {
        // Session has started
        setIsBeforeStartTime(false)
        setWaitTimeMs(0)
        setTimeUntilStart(null)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [sessionStartTime])

  // getImageLink expects a string (media ID), not an object
  const mentorImageUrl = mentorProfilePicId 
    ? getImageLink(mentorProfilePicId)
    : null

  // Don't render if drawer is collapsed
  if (isCollapsed) {
    return null
  }

  return (
    <Drawer
      anchor="right"
      open={open && !isCollapsed}
      variant="temporary"
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better mobile performance
        BackdropProps: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
      sx={{
        zIndex: 1300, // Higher z-index to ensure visibility
        '& .MuiDrawer-paper': {
          width: { xs: '90%', sm: 380 },
          maxWidth: 400,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderLeft: '2px solid rgba(139, 92, 246, 0.3)',
          boxShadow: '-8px 0 40px rgba(139, 92, 246, 0.25), 0 0 60px rgba(139, 92, 246, 0.1)',
          padding: { xs: '20px 16px', sm: '24px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          height: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      }}
    >
      {/* Header with collapse/close button - Always visible when drawer is open */}
      <Box sx={{ position: 'relative' }}>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: { xs: 8, md: -8 },
              right: { xs: 8, md: -8 },
              color: 'rgba(0, 0, 0, 0.5)',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              width: { xs: '44px', md: '40px' },
              height: { xs: '44px', md: '40px' },
              '&:hover': {
                background: 'rgba(139, 92, 246, 0.1)',
                color: '#8b5cf6',
                transform: { xs: 'scale(1.05)', md: 'scale(1.1)' },
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              transition: 'all 0.2s ease',
              touchAction: 'manipulation',
            }}
            title={isCollapsed ? "Expand drawer" : "Close drawer"}
          >
            <CloseIcon sx={{ fontSize: { xs: '24px', md: '20px' } }} />
          </IconButton>
        )}
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
              fontSize: { xs: '20px', md: '24px' },
            }}
          >
            {showOnlySupport ? 'Session Support' : 'Welcome to Your Session'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: { xs: '13px', md: '14px' },
            }}
          >
            {showOnlySupport 
              ? 'Need help during your session?'
              : 'Please wait while we prepare everything'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(139, 92, 246, 0.2)' }} />

      {/* Countdown Timer - Hidden in support-only mode */}
      {!showOnlySupport && isBeforeStartTime && timeUntilStart && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
            borderRadius: '16px',
            padding: '20px',
            textAlign: 'center',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <AccessTimeIcon sx={{ color: '#8b5cf6', fontSize: 24 }} />
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(0, 0, 0, 0.7)',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Session Starts In
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: 'monospace',
              letterSpacing: { xs: '2px', md: '4px' },
              fontSize: { xs: '32px', md: '48px' },
            }}
          >
            {timeUntilStart}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(0, 0, 0, 0.5)',
              fontSize: '12px',
              display: 'block',
              mt: 1,
            }}
          >
            Minutes : Seconds
          </Typography>
        </Box>
      )}

      {/* Waiting Engagement - Music/Games while waiting (show if wait > 2 mins) */}
      {!showOnlySupport && isBeforeStartTime && waitTimeMs > 120000 && (
        <Suspense 
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#8b5cf6' }} />
            </Box>
          }
        >
          <WaitingEngagement 
            waitTimeMs={waitTimeMs}
            minWaitThreshold={120000}
            engagementConfig={engagementConfig}
            sessionSlug={sessionSlug}
            bookedEventId={bookedEventId}
          />
        </Suspense>
      )}

      {/* Practitioner/Mentor Card - Hidden in support-only mode */}
      {!showOnlySupport && !hasMentorJoined && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            borderRadius: '20px',
            padding: { xs: '16px', md: '20px' },
            border: '1px solid rgba(139, 92, 246, 0.15)',
            boxShadow: '0 8px 24px rgba(139, 92, 246, 0.1), 0 2px 8px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative gradient bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #8b5cf6 100%)',
            }}
          />

          {/* Practitioner Label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalHospitalIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#8b5cf6',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Your Practitioner
            </Typography>
          </Box>

          {/* Practitioner Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            {/* Large Avatar */}
            <Box sx={{ position: 'relative' }}>
              {mentorImageUrl ? (
                <Avatar
                  src={mentorImageUrl}
                  alt={mentorName}
                  sx={{
                    width: { xs: 64, md: 72 },
                    height: { xs: 64, md: 72 },
                    border: '3px solid white',
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.25), 0 0 0 2px rgba(139, 92, 246, 0.3)',
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: { xs: 64, md: 72 },
                    height: { xs: 64, md: 72 },
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    border: '3px solid white',
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.25), 0 0 0 2px rgba(139, 92, 246, 0.3)',
                  }}
                >
                  <PersonIcon sx={{ fontSize: { xs: 32, md: 36 }, color: 'white' }} />
                </Avatar>
              )}
              {/* Verified badge */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  background: '#10b981',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 6px rgba(16, 185, 129, 0.4)',
                }}
              >
                <VerifiedIcon sx={{ fontSize: 14, color: 'white' }} />
              </Box>
            </Box>

            {/* Name and credentials */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1f2937',
                  fontSize: { xs: '17px', md: '19px' },
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
              >
                {mentorName || 'Your Doctor'}
              </Typography>
              <Chip
                label="Verified Professional"
                size="small"
                sx={{
                  height: '22px',
                  fontSize: '10px',
                  fontWeight: 600,
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: '#059669',
                  border: 'none',
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            </Box>
          </Box>
          
          {/* Status indicator */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)',
              borderRadius: '12px',
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              border: '1px solid rgba(245, 158, 11, 0.15)',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#f59e0b',
                animation: 'pulse 2s ease-in-out infinite',
                boxShadow: '0 0 8px rgba(245, 158, 11, 0.6)',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '50%': {
                    opacity: 0.6,
                    transform: 'scale(0.9)',
                  },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#92400e',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Will be joining shortly...
            </Typography>
          </Box>
          
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 2,
              color: 'rgba(0, 0, 0, 0.5)',
              fontSize: '12px',
              lineHeight: 1.6,
            }}
          >
            Please stay on this page. Your mentor will join shortly and you'll be notified when they arrive.
          </Typography>
        </Box>
      )}

      {/* Support QR Section - Using common SupportQRCode component */}
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          padding: { xs: '16px', md: '20px' },
          border: '1px solid rgba(139, 92, 246, 0.15)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        }}
      >
        <SupportQRCode
          qrCodeMediaId={supportQRCodeMediaId}
          qrCodeUrl={supportQRCodeUrl}
          title="Need Help?"
          subtitle="Scan the QR code below to reach out to our support team"
          size="medium"
        />
        
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 2,
            color: 'rgba(0, 0, 0, 0.5)',
            fontSize: '11px',
            textAlign: 'center',
          }}
        >
          Our team is here to help 24/7
        </Typography>
      </Box>

      {/* Info Box */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(139, 92, 246, 0.1)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: '12px',
            lineHeight: 1.6,
            display: 'block',
          }}
        >
          💡 <strong>Tip:</strong> Make sure your camera and microphone are enabled for the best experience. You can test them before the session starts.
        </Typography>
      </Box>
    </Drawer>
  )
}

export default WaitingDrawer

